import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X, Loader2, Mail, Check } from "lucide-react";
import { ResendEmailPreview } from "./ResendEmailPreview";

const KIND_LABEL = {
  exterior: "Exterior view",
  interior: "Interior view",
  bathroom: "New bathroom",
  kitchen: "New kitchen",
  planning: "Planning render",
};

// Modal shown when the office clicks "Resend" on a client row. Loads the list of AI
// visuals available for the quote and lets the admin pick which one leads the email
// (as the hero image). For quotes with a single render the picker is skipped.
export function ResendCoverPickerModal({ open, quote, onClose, onSent }) {
  const [renders, setRenders] = useState(null); // {kind, reference, available: [{kind,url}]}
  const [loading, setLoading] = useState(false);
  const [pick, setPick] = useState("exterior");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !quote?.quote_id) return;
    setRenders(null); setLoading(true); setPick("exterior"); setMessage("");
    api.get(`/admin/quote/${encodeURIComponent(quote.quote_id)}/renders`)
      .then(r => {
        setRenders(r.data);
        const first = r.data?.available?.[0]?.kind;
        if (first) setPick(first);
      })
      .catch(() => toast.error("Couldn't load visuals for this quote"))
      .finally(() => setLoading(false));
  }, [open, quote?.quote_id]);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/admin/quote/${encodeURIComponent(quote.quote_id)}/resend`,
        { message: message.trim() }, { params: { hero: pick } });
      toast.success(r.data.sent ? `Quote re-sent to ${r.data.to} (hero: ${r.data.hero})${r.data.message ? " with your note" : ""}` : "Could not send — check Gmail");
      onSent && onSent();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resend");
    }
    setSending(false);
  };

  if (!open) return null;
  const available = renders?.available || [];
  const isPlanning = renders?.kind === "planning";

  return (
    <div data-testid="resend-cover-modal" className="fixed inset-0 z-50 bg-[#12263A]/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E7EB]">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold">Resend quote</div>
            <div className="text-base font-black text-[#12263A]">{quote?.reference} · {quote?.email}</div>
          </div>
          <button data-testid="resend-cover-close" onClick={onClose} className="text-[#6B7280] hover:text-[#12263A]"><X size={18}/></button>
        </div>

        <div className="p-5 overflow-y-auto grid gap-5 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
          <div>
          {loading && (
            <div className="text-sm text-[#6B7280] flex items-center gap-2"><Loader2 size={14} className="animate-spin"/> Loading visuals…</div>
          )}
          {!loading && available.length === 0 && (
            <div className="text-sm text-[#C0392B] bg-[#FDECEA] border border-[#F5C6C0] p-3">
              No AI visuals are ready for this quote yet. Wait for the renders to finish or regenerate them from the Job File before resending.
            </div>
          )}
          {!loading && available.length > 0 && (
            <>
              <div className="text-xs text-[#6B7280] mb-3">
                {isPlanning
                  ? "This is a planning-permission quote — the render will be attached as-is."
                  : "Pick which visual leads the email (shown large at the top). The others will be included below — the preview on the right updates live."}
              </div>
              <div className={`grid gap-3 ${available.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
                {available.map(a => {
                  const selected = pick === a.kind;
                  return (
                    <button key={a.kind} type="button"
                      data-testid={`resend-cover-pick-${a.kind}`}
                      onClick={() => setPick(a.kind)}
                      className={`relative border-2 text-left overflow-hidden group ${selected ? "border-[#C79A3B]" : "border-[#E5E7EB] hover:border-[#C79A3B]/60"}`}>
                      <div className="aspect-[4/3] bg-[#F5F5F5]">
                        <img src={a.url} alt={KIND_LABEL[a.kind] || a.kind}
                          className="w-full h-full object-cover"
                          onError={e => { e.currentTarget.style.display = "none"; }} />
                      </div>
                      <div className="px-2 py-1.5 flex items-center justify-between">
                        <span className="text-xs font-bold text-[#12263A]">{KIND_LABEL[a.kind] || a.kind}</span>
                        {selected && <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-[#C79A3B]"><Check size={10}/>Cover</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}
          {!loading && (available.length > 0 || isPlanning) && (
            <div className="mt-4" data-testid="resend-message-wrap">
              <label htmlFor="resend-message" className="text-[10px] uppercase tracking-widest text-[#6B7280] font-bold block mb-1">
                Personal line <span className="normal-case tracking-normal font-medium">(optional — shown at the top of the email)</span>
              </label>
              <textarea id="resend-message" data-testid="resend-message" value={message} maxLength={400} rows={3}
                onChange={e => setMessage(e.target.value)}
                placeholder="e.g. Hi Sam, just checking in — happy to talk through any of this on the phone."
                className="w-full border border-[#E5E7EB] px-2.5 py-2 text-sm focus:border-[#C79A3B] outline-none resize-none" />
              <div className="flex items-center justify-between mt-1">
                <div className="flex flex-wrap gap-1">
                  {["Just checking in — any questions?", "Following up on your quote — happy to chat.", "Here's your quote again, as promised on the phone."].map(t => (
                    <button key={t} type="button" data-testid="resend-message-tpl" onClick={() => setMessage(t)}
                      className="text-[10px] font-bold px-1.5 py-0.5 border border-[#E5E7EB] text-[#6B7280] hover:border-[#C79A3B] hover:text-[#12263A]">{t}</button>
                  ))}
                </div>
                <span className="text-[10px] text-[#9CA3AF] tabular-nums shrink-0" data-testid="resend-message-count">{message.length}/400</span>
              </div>
            </div>
          )}
          </div>
          <ResendEmailPreview quoteId={quote?.quote_id} hero={pick} message={message}
            enabled={!loading && (available.length > 0 || isPlanning)} />
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E5E7EB] bg-[#FAFAFA]">
          <button data-testid="resend-cover-cancel" onClick={onClose} className="text-xs font-bold uppercase tracking-widest px-3 py-2 text-[#6B7280] hover:text-[#12263A]">Cancel</button>
          <button data-testid="resend-cover-send" onClick={send}
            disabled={sending || (available.length === 0 && !isPlanning)}
            className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest bg-[#12263A] text-white px-4 py-2 hover:bg-[#0B1B2A] disabled:opacity-40">
            {sending ? <Loader2 size={12} className="animate-spin"/> : <Mail size={12}/>}
            {isPlanning ? "Resend" : `Resend with ${KIND_LABEL[pick] || pick}`}
          </button>
        </div>
      </div>
    </div>
  );
}
